import { ApiError } from '@/services/api/types'
import { PayloadAction, createAsyncThunk, createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { User, AvailableRoles, StudentProfile, hasPermission, Permissions } from '@/interfaces/User'
import { MetaData } from '@/interfaces/Firestore'
import { EnumAccountType } from '@/components/auth/on-boarding/types'
import * as authApi from '@/services/api/endpoints/auth'
import { UserRole } from '@/services/api/endpoints/auth'
import * as studentProfileApi from '@/services/api/endpoints/studentProfile'
import { fetchEmployerProfile } from '@/store/employer/employerSlice'
import { fetchLawyerProfile } from '@/store/immigration/immigrationSlice'
import { fetchSchoolAdminProfile } from '@/store/schoolAdmin/schoolAdminSlice'
import { Institution } from '@/interfaces/Institution'

const BACKEND_ROLE_TO_ACCOUNT_TYPE: Record<UserRole, EnumAccountType> = {
  EMPLOYER: EnumAccountType.EMPLOYER,
  IMMIGRATION_LAWYER: EnumAccountType.LAWYER,
  SCHOOL_ADMIN: EnumAccountType.SCHOOL_ADMIN,
  STUDENT: EnumAccountType.STUDENT,
  SUPER_ADMIN: EnumAccountType.SUPER_ADMIN,
}

/**
 * GET /auth/me only returns identity + role, not the rich profile fields (name,
 * institution, etc.) that the existing `User` interface expects - those are
 * populated by the role-specific profile fetch (e.g. GET /student/profile) added
 * in the next migration branch. Until then this maps what we have onto `User`
 * with empty placeholders so the many existing consumers of `selectUser` keep
 * compiling and rendering (just with blank name/institution fields).
 */
function mapAuthenticatedUserToUser(me: authApi.AuthenticatedUser): User {
  return {
    email: me.email,
    enterprise: '',
    firstName: '',
    id: me.id,
    institution: me.institutionId,
    lastName: '',
    metaData: { createdAt: null, lastUpdatedBy: '', updatedAt: null } as unknown as MetaData,
    type: BACKEND_ROLE_TO_ACCOUNT_TYPE[me.role] ?? EnumAccountType.NONE,
  }
}

interface UserState {
  applicantsLoading: boolean,
  applicantsProfiles: Map<string, User&StudentProfile>,
  error: string | null,
  institution: Institution | null,
  institutions: Institution[] | null,
  isUserLoading: boolean
  user: User | null,
  /** Why the signed-in user couldn't be loaded: 'closed' (403), or 'failed' (server or network, worth retrying). */
  userLoadError: 'closed' | 'failed' | null,
}

const initialState: UserState = {
  applicantsProfiles: new Map(),
  applicantsLoading: false,
  error: null,
  institution: null,
  institutions: null,
  isUserLoading: true,
  user: null,
  userLoadError: null,
}

const ME_RETRY_DELAYS_MS = [1000, 2000]

export const fetchCurrentUser = createAsyncThunk('user/fetchCurrentUser', async (_, { rejectWithValue }) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await authApi.getMe()
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0
      const isBlip = status === 0 || status >= 500
      if (!isBlip || attempt >= ME_RETRY_DELAYS_MS.length) {
        return rejectWithValue({ message: err instanceof ApiError ? err.message : 'Failed to fetch current user', status })
      }
      await new Promise((resolve) => setTimeout(resolve, ME_RETRY_DELAYS_MS[attempt]))
    }
  }
})

/** No student-profile thunk existed anywhere - getMyStudentProfile was defined
 * but never called from Redux, matching the pattern for the other three
 * personas below (each already fetches its own profile for its own dashboard;
 * this reuses that instead of firing a redundant second request). */
export const fetchStudentProfile = createAsyncThunk('user/fetchStudentProfile', async (_, { rejectWithValue }) => {
  try {
    return await studentProfileApi.getMyStudentProfile()
  } catch (err) {
    return rejectWithValue('Failed to fetch student profile')
  }
})

export const userSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isUserLoading = true
        state.userLoadError = null
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = mapAuthenticatedUserToUser(action.payload)
        state.isUserLoading = false
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isUserLoading = false
        const { status } = (action.payload as { status?: number } | undefined) ?? {}
        state.userLoadError = status === 403 ? 'closed' : 'failed'
      })
      /** GET /auth/me never returns a real name/avatar (see the comment on
       * mapAuthenticatedUserToUser above) - each persona's own profile fetch
       * does, so once any of them resolves, backfill the shared user object
       * that every header/sidebar actually reads from. */
      .addMatcher(
        isAnyOf(fetchEmployerProfile.fulfilled, fetchLawyerProfile.fulfilled, fetchSchoolAdminProfile.fulfilled, fetchStudentProfile.fulfilled),
        (state, action) => {
          if (!state.user) return
          state.user.firstName = action.payload.firstName || state.user.firstName
          state.user.lastName = action.payload.lastName || state.user.lastName
          state.user.photoURL = action.payload.avatarUrl || state.user.photoURL
        },
      )
  },
  initialState,
  name: 'user',
  reducers: {
    setApplicantProfile: (state, action: PayloadAction<User&StudentProfile>) =>{
      const id = action.payload.id
      state.applicantsProfiles.set(id, action.payload)
    },
    setApplicantsLoading: (state, action: PayloadAction<boolean>) => {
      state.applicantsLoading = action.payload
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isUserLoading = false
    },
    setInstitution: (state, action: PayloadAction<Institution | null>) => {
      state.institution = action.payload
    },
    setInstitutions: (state, action: PayloadAction<Institution[] | null>) => {
      state.institutions = action.payload
    },
  },
})

export const { setUser, setInstitution, setInstitutions, setApplicantProfile, setApplicantsLoading } = userSlice.actions

export const selectUser = (state: { user: UserState }): User | null => state.user.user
export const selectUserLoadError = (state: { user: UserState }): UserState['userLoadError'] => state.user.userLoadError
export const selectIsUserLoading = (state: { user: UserState }): boolean => state.user.isUserLoading
export const selectInstitution = (state: { user: UserState }): Institution | null => state.user.institution
export const selectInstitutions = (state: { user: UserState }): Institution[] | null => state.user.institutions
export const selectApplicants = (state: {user: UserState}): Map<string, User&StudentProfile> => state.user.applicantsProfiles
export const selectApplicantsArray = createSelector(selectApplicants, (applicants)=>{
  return Array.from(applicants.values())
})
export const selectApplicantsById = (state: {user: UserState}, applicantId: string):User&StudentProfile | undefined => state.user.applicantsProfiles.get(applicantId)
export const selectApplicantsLoading = (state: {user: UserState}): boolean => state.user.applicantsLoading

export const selectUserRoles = (state: { user: UserState }): AvailableRoles[] => state.user.user?.roles ?? []

export const selectHasPermission = (permission: Permissions) =>
  createSelector([selectUserRoles], (roles) => hasPermission(roles, permission))

export default userSlice.reducer
